import {Image} from '@shopify/hydrogen';

import {Dialog} from '@headlessui/react';
import sanitizeHtml from 'sanitize-html';

import {ModalHeader} from '../ProductModal/ModalHeader';
import Modal from '../Modal';
import classNames from 'classnames';
import Button from 'components/Button.client';
import {usePageContext} from 'contexts/PageContext.client';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {MonorailEvent, trackEvent} from 'utils/monorail.client';

interface AmazonAffiliateLinkModalProps {
  show: boolean;
  closeModal: () => void;
  imageUrl?: string;
  title: string;
  url: string;
  amazonPrice?: string;
  description?: string;
}

export default function AmazonAffiliateLinkModal({
  show,
  closeModal,
  imageUrl,
  title,
  description,
  url,
  amazonPrice,
}: AmazonAffiliateLinkModalProps) {
  const pageCtx = usePageContext();
  const logger = useErrorLogger();

  function handleClick() {
    window.open(url);
    trackAmazonCardClicked();
  }

  function trackAmazonCardClicked() {
    try {
      trackEvent({
        event: MonorailEvent.AmazonAffiliateBuyOnAmazonClicked,
        data: {
          pageId: pageCtx.id,
          linkUrl: url,
          linkTitle: title,
        },
      });
    } catch (error) {
      logger.notify(
        new Error(
          `event tracking error: ${MonorailEvent.AmazonAffiliateBuyOnAmazonClicked}: ${error}`,
        ),
      );
    }
  }

  return (
    <Modal show={show} closeModal={closeModal}>
      <div
        data-testid="amazon-affiliate-link-modal"
        className={classNames(
          'inline-block',
          'w-full',
          'transform',
          'bg-white',
          'text-left',
          'align-bottom',
          'shadow-xl',
          'transition-all',
          'md:my-8',
          'md:max-w-[40rem]',
          'md:rounded-2xl',
          'md:align-middle',
        )}
      >
        <div className="divide-y">
          <ModalHeader title={title} closeModal={closeModal} />
          <div
            className={classNames(
              'max-h-modal',
              'gap-8',
              'overflow-auto',
              'p-6',
              'md:grid',
              'md:grid-cols-2',
            )}
          >
            {!!imageUrl && (
              <div>
                <Image
                  src={imageUrl}
                  alt="Product image"
                  width={200}
                  height={200}
                  className={classNames(
                    'w-[80vw]',
                    'snap-start',
                    'md:flex-shrink-none',
                    'flex-shrink-0',
                    'h-full',
                    'object-cover',
                    'object-center',
                    'md:col-span-2',
                    'md:w-full',
                    'md:h-auto',
                  )}
                />
              </div>
            )}
            <div className={classNames('mt-2', 'flex', 'flex-col')}>
              <Dialog.Title
                as="h3"
                className={classNames(
                  'text-2xl',
                  'font-medium',
                  'leading-8',
                  'text-gray-900',
                )}
              >
                {title}
              </Dialog.Title>
              {!!amazonPrice && <div className="text-xl">{amazonPrice}</div>}
              {!!description && (
                <div
                  className={classNames(
                    'prose',
                    'mt-2',
                    'border-t',
                    'border-gray-200',
                    'pt-6',
                    'text-sm',
                    'text-black',
                  )}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(description),
                  }}
                />
              )}
              <Button
                testId="buy-on-amazon-link"
                label="Buy on Amazon"
                handleClick={handleClick}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
